/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostBinding,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
  Optional,
  ViewChild,
} from '@angular/core';
import {
  fromEvent,
  merge,
  Observable,
  of,
  partition,
  Subject,
  SubscriptionLike,
} from 'rxjs';
import {
  debounceTime,
  filter,
  take,
  takeUntil,
  delay,
  map,
  scan,
  throttleTime,
  pairwise,
  catchError,
} from 'rxjs/operators';
import {
  AddressChangeEvent,
  AnnotationChangedEvent,
  AnnotationCreatedEvent,
  AnnotationRemovedEvent,
  BookLoadErrorEvent,
  BookLoadEvent,
  BookmarkStateChangeEvent,
  HighlightClickEvent,
  ItemLoadEvent,
  ItemLoadStartEvent,
  LibConfig,
  PageInfoChangeEvent,
  PageTapEvent,
  SelectionChangeEvent,
  SettingsChangeEvent,
  ViewerComponent,
  AnnotationType,
  Annotation,
  ItemLoadErrorEvent,
  ItemLoadErrorCode,
  Theme,
  PagingMode,
  SettingValues,
  PageChangeCanceledEvent,
  Direction,
  PageChangeCancelCode,
  Address,
  BookItem,
  ZoomScaleChangeEvent,
  parseBukURL,
  BookType,
  Settings,
} from '@bukio/viewer';
import { Clipboard } from '@angular/cdk/clipboard';

import { clamp } from 'shared/utils';
import { createSNSShareUrl, createSNSShareWindowFeatures } from 'shared/utils';
import {
  APIError,
  MemosAPIService,
  SharedAuthService,
  SharedUserService,
} from 'shared/services';
import { AlertService, DialogService, ToastService } from 'shared/ui';

import { IntervalLoggerService } from '../../services/interval-logger.service';
import { BroadcastService } from '../../services/broadcast.service';
import { MAX_ANNOTATION_LENGTH_EXCEEDED_ERROR } from '../../services/annotations.service';
import { EventBusService } from '../../services/event-bus.service';
import { BukViewerSearchService } from '../../services/buk-viewer-search.service';
import { StorageService } from '../../services/storage.service';

import { getActiveIframes, getIIDFromRange } from '../../utils/buk-viewer';

import {
  ContextMenuItem,
  HighlightAction,
  SelectionContext,
} from '../context-menu/context-menu.component';

import { Book, BukJSON } from '../../models/book.model';
import { createSharingURL, isSharedAddress } from '../../utils/address';
import { DOCUMENT, Location } from '@angular/common';
import { Router } from '@angular/router';
import {
  fromKeydownEvent,
  fromPageChangeKeydownEvent,
  fromUserScrollEvent,
  scanNavigationChangeResult,
} from '../../utils/rxjs';
import {
  createPermissionAnchor,
  isShortcutKeyboardEvent,
} from '../../utils/misc';
import { canOpenBookWithURL } from '../../utils/url';
import {
  VIEWER_ENVIRONMENT,
  ViewerModuleEnvironment,
} from '../../viewer.module';
import { AnnotationsV2Service } from '../../services/annotations-v2.service';
import {
  BookmarksService,
  convertBookmarkToViewerAnnotation,
} from '../../services/bookmarks.service';
import { AnalyticsService } from '../../services/analytics.service';

// import { MemoDialogComponent } from '../../dialogs/memo-dialog/memo-dialog.component';
import { HeaderMenuItem } from '../header/header.component';
import { SettingsDialogComponent } from '../../dialogs/settings-dialog/settings-dialog.component';
import {
  HIGHLIGHT_COLORS,
  HighlightColor,
} from '../../constants/highlight-colors';
import { BookInfoDialogComponent } from '../../dialogs/book-info-dialog/book-info-dialog.component';
import { FooterMenuItem } from '../footer/footer.component';
import { getMemoGroupType } from '../../utils/memo';
import { PreviewEndDialogComponent } from '../../dialogs/preview-end-dialog/preview-end-dialog.component';
import { ReadingModeDialogComponent } from '../../dialogs/reading-mode-dialog/reading-mode-dialog.component';
import { UIStateService } from '../../services/ui-state.service';
import { PDFDrawingService } from '../../services/pdf-drawing.service';
import { BookFeaturesStoreService } from '../../services/book-features-store.service';
import { SearchDialogComponent } from '../../dialogs/search-dialog/search-dialog.component';
import {
  extendSelectionToWord,
  isRangeAtPageBoundary,
  modifyRangeWithinViewport,
} from '../../utils/range';
import { CrossBrowsing } from '../../constants/cross-browsing';
import { MemoDialogComponent } from '../../dialogs/memo-dialog/memo-dialog.component';
import { Config } from '../../constants/config';

export const USER_HIGHLIGHT_STYLES = Object.entries(HIGHLIGHT_COLORS).map(
  ([key, color]) => ({
    styleClass: key,
    style: `background: ${color}`,
  })
);

export const USER_HIGHLIGHT_STYLE_CLASSES = USER_HIGHLIGHT_STYLES.map(
  (s) => s.styleClass
);
export const SHARED_HIGHLIGHT_STYLE_CLASS = 'bukh-color-shared';

export const MOMO_MEMO_STYLE_CLASS = 'bukh-color-momo-m';

const MEMO_INDICATOR_CLASS = 'buk-memo-indicator';
const MEMO_INDICATOR_TYPE_MINE = 'mine';
const MEMO_INDICATOR_TYPE_SHARED = 'shared';
const MEMO_INDICATOR_TYPE_MINE_SHARED = 'mine_shared';
const MEMO_INDICATOR_TYPE_COMMENTARY = 'commentary';
const MEMO_INDICATOR_TYPE_MINE_COMMENTARY = 'mine_commentary';

export const MOMO_HIGHLIGHT_STYLE_CLASS_COMMENTARY =
  'bukh-color-momo-commentary';
export const MOMO_HIGHLIGHT_STYLE_CLASS_MEMBER = 'bukh-color-momo-member';
export const MOMO_HIGHLIGHT_STYLE_CLASS_OVERLAP2 = 'bukh-color-momo-overlap2';
export const MOMO_HIGHLIGHT_STYLE_CLASS_OVERLAP5 = 'bukh-color-momo-overlap5';
export const MOMO_HIGHLIGHT_STYLE_CLASS_OVERLAP10 = 'bukh-color-momo-overlap10';

const FOCUSED_MEMO_CLASS_SINGLE = 'buk-memo-focused-single';
const FOCUSED_MEMO_CLASS_DOUBLE = 'buk-memo-focused-double';

export const VIEWER_CONFIG: LibConfig = {
  contentsBaseURL: 'https://contents.buk.io',
  fonts: {
    en: {
      fontFaces: [
        'https://fonts.googleapis.com/css?family=Noto+Serif%7COpen+Sans%7CTinos%7CRoboto&subset=latin,latin-ext,latin,latin-ext,latin,latin-ext,latin,latin-ext',
      ],
      fontFamilies: ['Noto Serif', 'Roboto', 'Open Sans', 'Tinos'],
    },
    ko: {
      fontFaces: [
        'https://cdn.buk.io/css/fonts/kopubbatang.css',
        'https://cdn.buk.io/css/fonts/nanumgothic.css',
        'https://cdn.buk.io/css/fonts/nanummyeongjo.css',
      ],
      fontFamilies: ['Nanum Gothic', 'Nanum Myeongjo', 'KoPub Batang'],
    },
  },
  highlightStyles: [
    ...USER_HIGHLIGHT_STYLES,
    {
      styleClass: SHARED_HIGHLIGHT_STYLE_CLASS,
      style: 'background: #5EFFFF33;cursor: auto;',
    },

    {
      styleClass: 'bukh-color-reading-g',
      style: 'box-shadow: inset 0 -0.7em #E0FF004D; mix-blend-mode: multiply;', // 서버에서 실제로 사용하는 클래스명
    },
    {
      styleClass: MOMO_HIGHLIGHT_STYLE_CLASS_COMMENTARY,
      style:
        'box-shadow: inset 0 -2px rgba(0, 224, 255, 0.6); cursor: auto;' /*, 0px 2px rgba(94, 255, 255, 0.4)'*/,
    },
    {
      styleClass: MOMO_HIGHLIGHT_STYLE_CLASS_MEMBER,
      style: 'box-shadow: inset 0 -2px rgba(20, 255, 0, 1); cursor: auto;',
    },
    {
      styleClass: MOMO_HIGHLIGHT_STYLE_CLASS_OVERLAP2,
      style: 'box-shadow: inset 0 -2px rgba(20, 255, 0, 0.4); cursor: auto;',
    },
    {
      styleClass: MOMO_HIGHLIGHT_STYLE_CLASS_OVERLAP5,
      style: 'box-shadow: inset 0 -2px rgba(20, 255, 0, 0.6); cursor: auto;',
    },
    {
      styleClass: MOMO_HIGHLIGHT_STYLE_CLASS_OVERLAP10,
      style: 'box-shadow: inset 0 -2px rgba(20, 255, 0, 0.8); cursor: auto; ',
    },
  ],
  contentsStyles: [
    `[data-bukv-theme="light"] { --bukv-bg-color: #fff }`,
    `[data-bukv-theme="sepia"] { --bukv-bg-color: #f8f6f1 }`,
    `[data-bukv-theme="dark"] { --bukv-bg-color: #282828 }`,
    `x.buk-temp-comment-highlight { background-color: #5EFFFF33 !important; padding: 2px 0; }`,
    `:root:not([data-bukv-is-cover="true"]) {padding-top: 10px;}`,
    `::selection {background-color: #5EFFFF33;}`,
  ],
  enablePinchZoom: true,
};

const DEFAULT_SETTINGS: SettingValues = {
  theme: Theme.Light,
  pagingMode: PagingMode.Page,
  pageAnimation: true,
  multiColumn: false,
  fontFace: 'default',
  fontSize: 100,
  lineHeight: 100,
  paragraphSpace: 100,
  bodySpace: 100,
  clickToPlayMediaOverlay: false,
  zoomKeep: false,
};

function getGATrackingLabelOfContextMenuItem(menu: ContextMenuItem): string {
  switch (menu) {
    case ContextMenuItem.CopyText:
      return 'copy-text';
    case ContextMenuItem.CopyURL:
      return 'copy-url';
    case ContextMenuItem.ShareTwitter:
    case ContextMenuItem.ShareFacebook:
      return 'share';
    case ContextMenuItem.MediaOverlay:
      return 'play-media-overlay';
    case ContextMenuItem.Memo:
      return 'memo';
    case ContextMenuItem.Comment:
      return 'comment';
    case ContextMenuItem.SelectContinuously:
      return 'select-continuously';
  }
}

const OVERRIDABLE_SETTING_KEYS: (keyof SettingValues)[] = [
  'theme',
  'fontSize',
  'pageAnimation',
  'pagingMode',
  'multiColumn',
];

function settingValuesFromObject(values: {
  [key: string]: string;
}): Partial<SettingValues> {
  const result = {} as any;

  OVERRIDABLE_SETTING_KEYS.forEach((key) => {
    const value = values[key];

    if (value == null) {
      return;
    }

    let normalizedValue: any;

    switch (key) {
      case 'theme': {
        if (/^(light|sepia|dark)$/.test(value)) {
          normalizedValue = value;
        }
        break;
      }
      case 'fontSize': {
        if (/^\d+$/.test(value)) {
          normalizedValue = clamp(50, parseInt(value), 200);
        }
        break;
      }
      case 'pagingMode': {
        if (/^(page|scroll)$/.test(value)) {
          normalizedValue = value;
        }
        break;
      }
      case 'pageAnimation':
      case 'multiColumn': {
        if (/^(true|false)$/.test(value)) {
          normalizedValue = value === 'true';
        }
        break;
      }
    }

    if (normalizedValue != null) {
      result[key] = normalizedValue;
    }
  });

  return result;
}

function shouldPushState(currAddr: Address, prevAddr?: Address): boolean {
  if (!prevAddr) {
    return false;
  }

  if (prevAddr.bid !== currAddr.bid) {
    return true;
  }

  return false;
}

function onCopyContentDocument(event: ClipboardEvent): void {
  event.preventDefault();
}

@Component({
  selector: 'viewer-contents',
  templateUrl: './contents.component.html',
  styleUrls: ['./contents.component.scss'],
})
export class ContentsComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(ViewerComponent) _viewerComp!: ViewerComponent;
  @HostBinding('attr.data-book-type')
  get _bookType(): string | undefined {
    document.body.setAttribute('data-book-type', this._book?.meta.type ?? '');
    return this._book?.meta.type;
  }

  private unsubscriber: Subject<void> = new Subject<void>();
  private _locationChangeSubscription!: SubscriptionLike;

  public readonly _libConfig = VIEWER_CONFIG;
  public _selectionChange$ = new Subject<SelectionChangeEvent>();

  private _replaceBrowserURL$ = new Subject<{
    path: string;
    query?: string;
  } | null>();

  private _isContextMenuShowing = false;
  private _hideContextMenuTimeoutId?: number;

  private _pendingAnnotActions = new Map<
    string,
    (annotation: Annotation, error?: Error | APIError) => void
  >();

  private _book?: Book;
  private _isItemLoading = false;

  private _loadedItems!: BookItem[];

  private _sharedPageInfo: { pageCount: number; page: number } | null = null;
  private _initialAccessAddress: Address | null = null; // 초기 접속 주소 저장
  private _initialAccessPageInfo: { pageCount: number; page: number } | null =
    null; // 초기 접속 페이지 정보 저장
  private _shouldFixPagingMode = false;

  private _prevWindowSize?: { w: number; h: number };

  private _overrideSettings?: Partial<SettingValues>;

  private _address?: Address;

  private _currentTOCIndex = -1;

  private _frameBodyMutationObserver?: MutationObserver;
  private _isListeningZoomScrollEvent = false;

  private _referer?: Address;

  private _pendingHighlightAddress?: Address;

  /** 댓글 클릭으로 pending 하이라이트가 잡힌 로드에서는 referer(bukh-color-shared)를 그리지 않음 */
  private _suppressRefererHighlightForPendingComment = false;

  /** 헤더 전체댓글 버튼으로 패널이 열릴 때 다음 번 referer(bukh-color-shared) 하이라이트를 억제 */
  private _suppressNextRefererHighlight = false;

  private _continuouslySelectedRange?: Range;

  private _scrollZoomInfo?: { scrollLeft: number; scrollTop: number } | null =
    null;

  private _loadTimeout: any = null;
  private _isDrawingModeTransition = false; // 필기모드 전환 상태를 추적

  private _lastItemClickSource?: string;

  private _currentPage?: { pageCount: number; page: number }; // 현재 페이지 정보 저장

  private _currentZoomScale = 100;

  private _scrollRequired = false;
  private _pageTransitionSource?: string; // 댓글 패널 열기/닫기로 인한 페이지 전환임을 표시

  private _isAppEnvironment: boolean; // 앱 환경인지 여부

  constructor(
    private _memosAPIService: MemosAPIService,
    private _element: ElementRef,
    @Inject(DOCUMENT) private _document: Document,
    private _ngZone: NgZone,
    private _location: Location,
    private _router: Router,
    private _clipboard: Clipboard,
    private _$: BroadcastService,
    private _eventBusService: EventBusService,
    private _userService: SharedUserService,
    private _bukViewerSearchService: BukViewerSearchService,
    private _storageService: StorageService,
    private _loggerService: IntervalLoggerService,
    private _annotationsService: AnnotationsV2Service,
    private _bookmarksService: BookmarksService,
    @Inject(VIEWER_ENVIRONMENT) _environment: ViewerModuleEnvironment,
    private _dialogService: DialogService,
    private _toastService: ToastService,
    private _alertService: AlertService,
    private _analyticsService: AnalyticsService,
    private _authService: SharedAuthService,
    private _uiStateService: UIStateService,
    private _bookFeaturesStoreService: BookFeaturesStoreService,
    @Optional() private _pdfDrawingService?: PDFDrawingService
  ) {
    // 앱 환경 감지 (Android: window.bukApp, iOS: window.webkit?.messageHandlers?.bukHybrid)
    this._isAppEnvironment = !!(
      (window as any).bukApp ||
      (window as any).webkit?.messageHandlers?.bukHybrid
    );

    this._libConfig.bukJSONBaseURL = _environment.serverOrigin;

    if (self === top) {
      this._libConfig.initialSettings =
        this._storageService.loadSettings() ?? undefined;
    } else {
      this._libConfig.initialSettings = DEFAULT_SETTINGS;
    }

    this._libConfig.canChangeAddress = (newAddress): boolean => {
      return this._canChangeAddress(newAddress);
    };

    this._libConfig.contentsBaseURL = `https://${
      location.hostname.replace(/\./g, '') + location.port
    }.contents.buk.io`;

    this._replaceBrowserURL$
      .pipe(throttleTime(500, undefined, { leading: false, trailing: true }))
      .subscribe((url) => {
        url && this._location.replaceState(url.path, url.query);
      });

    fromEvent(window, 'resize')
      .pipe(debounceTime(500))
      .subscribe(() => {
        this._updateMemoIndicator();
      });

    const query = window.matchMedia('(orientation: landscape)');
    query.addEventListener('change', () => {
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 100);
    });

    fromEvent<KeyboardEvent>(document.documentElement, 'keydown')
      .pipe(takeUntil(this.unsubscriber))
      .subscribe((event) => {
        this._onKeydown(event);
      });
  }

  private _updateBrowserURL(newAddress: Address): void {
    const address = newAddress.clone();

    if (!address.query) {
      address.query = {};
    }

    if (this._annotationsService.getState().commentaryId) {
      address.query!['cId'] = this._annotationsService.getState().commentaryId;
      delete address.query.gId;
    } else {
      delete address.query.cId;
    }

    if (this._annotationsService.getState().groupId) {
      address.query!['gId'] = this._annotationsService.getState().groupId;
      delete address.query.cId;
    } else {
      delete address.query.gId;
    }

    if (this._uiStateService.isCommentaryEditor) {
      address.query!['commentaryEditor'] = true;
    }

    const viewerURL = address.toString();
    const browserURL = this._location.path();

    if (browserURL !== viewerURL) {
      const [path, query] = viewerURL.split('?');

      if (shouldPushState(newAddress, this._address)) {
        this._replaceBrowserURL$.next(null);
        this._location.go(path, query);
      } else {
        this._replaceBrowserURL$.next({ path, query });
      }
    } else {
      this._replaceBrowserURL$.next(null);
    }
  }

  ngOnInit(): void {
    this._annotationsService.state$
      .pipe(
        map((state) => [state.groupId, state.commentaryId]),
        pairwise(),
        filter(
          ([state1, state2]) =>
            state1[0] !== state2[0] || state1[1] !== state2[1]
        )
      )
      .subscribe((state) => {
        // anchor highlight 제거
        this._clearAnchorHighlight();

        if (
          this._referer?.query?.cPreview &&
          (state[0] != null || state[1] != null)
        ) {
          this._referer = undefined;
        }

        if (this._address) {
          if (this._currentPage) {
            if (this._book?.meta.type === BookType.PDF) {
              this._address.anchor = undefined;
            } else {
              this._address.anchor =
                (this._currentPage?.page - 1) / this._currentPage?.pageCount;
            }

            this._clearFocusedMemo();
          }

          this._updateBrowserURL(this._address);
        }
      });

    // 북마크 동기화
    this._annotationsService.itemsAnnotations$
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(() => {
        this._syncAnnotations();

        setTimeout(() => {
          this._updateMemoIndicator();
        });
      });

    this._bookmarksService.bookmarks$
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(() => {
        this._syncAnnotations();
      });

    // 선택, 선택 해제
    const [deselect$, select$] = partition(
      this._selectionChange$,
      (event) => !event.selection || !event.selection.range.toString().trim()
    );

    deselect$.subscribe(() => {
      this._onDeselectText();
    });

    select$.pipe(debounceTime(500)).subscribe((event) => {
      this._onSelectText(event);
    });

    // 페이지 이동
    merge(
      fromPageChangeKeydownEvent(this._document.documentElement).pipe(
        filter(({ event }) => (event.target as any).tagName !== 'BUKV-VIEWER'),
        map(({ direction }) => ({ direction }))
      ),
      this._eventBusService.on('PdfMenuComponent:pageChange'),
      this._eventBusService.on('PagerComponent:pageChange'),
      this._eventBusService.on('DrawingToolBarComponent:pageChange')
    )
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(({ direction }) => {
        if (direction === Direction.Next) {
          this._viewerComp?.nextPage();
        } else if (direction === Direction.Prev) {
          this._viewerComp?.prevPage();
        }
      });

    // 컨텍스트 메뉴
    this._eventBusService
      .on('ContextMenuComponent:highlight')
      .pipe(takeUntil(this.unsubscriber))
      .subscribe((event) => {
        this._onHighlightMenuClick(
          event.action,
          event.context,
          event.styleClass
        );
      });

    this._eventBusService
      .on('ContentsComponent:pageTransitionEnd')
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(({ source }) => {
        // source가 panelToggle인 경우만 댓글창 껏다 키는 경우에 해당함.
        if (source && !this._uiStateService.isRightPanelOpened) {
          this._clearAnchorHighlight();
        }

        if (this._scrollRequired) {
          this._scrollRequired = false;
          setTimeout(() => {
            getActiveIframes(this._element.nativeElement).forEach((iframe) => {
              const element = iframe.contentDocument?.querySelector(
                'x.buk-temp-comment-highlight'
              ) as HTMLElement;
              element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            });
          }, 200);
        }
      });

    // 그룹 변경 시 anchor highlight 제거
    this._eventBusService
      .on('ReadingModeDialogComponent:groupChanged')
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(() => {
        this._clearAnchorHighlight();
      });

    this._eventBusService
      .on('ContextMenuComponent:menuClick')
      .pipe(takeUntil(this.unsubscriber))
      .subscribe((event) => {
        this._onContextMenuClick(event.menu, event.context);
      });

    this._eventBusService
      .on('ContextMenuComponent:visibilityChange')
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(({ isVisible }) => {
        if (!isVisible) {
          this._onHideContextMenu();
        } else {
          this._onShowContextMenu();
        }
      });

    merge(
      this._eventBusService.on('HeaderComponent:menuClick'),
      this._eventBusService.on('FooterComponent:menuClick')
    )
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(({ menu }) => {
        switch (menu) {
          case HeaderMenuItem.Settings:
          case FooterMenuItem.Settings:
            this._openSettingsDialog();
            break;
          case HeaderMenuItem.TOC:
          case FooterMenuItem.TOC:
            this._openBookInfoDialog();
            break;
          case HeaderMenuItem.Bookmark:
            this._viewerComp?.toggleBookmark();
            break;
          case HeaderMenuItem.ReadingMode:
          case FooterMenuItem.ReadingMode:
            this._openReadingModeDialog();
            break;
        }
      });

    merge(
      this._eventBusService.on('PageNumberComponent:backButtonClick'),
      this._eventBusService.on('PdfMenuComponent:pageInputBlurred'),
      this._eventBusService.on('BookEndDialogComponent:otherSeriesClick'),
      this._eventBusService.on('ThumbnailDialogComponent:thumbnailClick'),
      this._eventBusService.on('PageBackButtonComponent:click'),
      this._eventBusService.on('PDFPageSliderComponent:pageChange'),
      this._eventBusService.on('BukPageSliderComponent:pageChange'),
      this._eventBusService.on('DocumentPageSliderComponent:pageChange')
    )
      .pipe(takeUntil(this.unsubscriber))
      .subscribe((event: { url: string; source?: string }) => {
        this._lastItemClickSource = event.source; // 소스 정보 저장 (없을 수 있음)
        this._openBook(event.url);
      });

    // 나의책 메모에서 본문보기 클릭한 경우 (하이라이트, 메모, 북마크, 필기중 메모만 링크이동후 하이라이트 되도록)
    this._eventBusService
      .on('AnnotationsComponent:itemClick')
      .pipe(takeUntil(this.unsubscriber))
      .subscribe((event: { url: string; source?: string }) => {
        this._lastItemClickSource = event.source; // 소스 정보 저장 (없을 수 있음)

        if (event.source === 'memo') {
          const address = parseBukURL(event.url);
          address.query = { highlight: '#5EFFFF33' };
          this._openBook(address);
        } else {
          this._openBook(event.url);
        }
      });

    this._eventBusService
      .on('SearchDialogComponent:resultClick')
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(({ url, source }) => {
        const address = parseBukURL(url);
        address.query = { highlight: 'rgba(255, 255, 0, 0.4)' };
        this._lastItemClickSource = source || 'searchResult';
        this._openBook(address);
      });

    merge(
      this._eventBusService.on('FixedLayoutPageControlComponent:changeZoom'),
      this._eventBusService.on('PdfMenuComponent:changeZoom')
    )
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(({ scale }) => {
        this._viewerComp.zoom(scale);
      });

    this._eventBusService
      .on('BookEndToastComponent:goToCoverClick')
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(() => {
        const address = new Address(
          this._book!.meta.bid,
          this._book!.items[0].iid,
          undefined,
          this._address?.query?.l ? { l: this._address.query.l } : undefined
        );

        this._openBook(address);
      });

    this._eventBusService
      .on('LangaugeSelectorComponent:change')
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(({ langs }) => {
        const address = this._viewerComp?.getCurrentAddress()?.clone();

        if (!address) {
          return;
        }

        if (!address.query) {
          address.query = {};
        }

        address.query.l = langs.join(',');

        this._openBook(address);
      });

    // 책 내 검색
    this._bukViewerSearchService.search$
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(({ searchId, keyword }) => {
        const cancelFn = this._viewerComp.search(keyword, (results) => {
          this._eventBusService.fire('ContentsComponent:searchDone', {
            searchId,
            keyword,
            results,
          });
        });

        this._eventBusService.fire('ContentsComponent:searchStart', {
          searchId,
          keyword,
          cancelFn,
        });
      });

    // this._eventBusService
    //   .on('ReadingModeMemoComponent:seeDetailsButtonClick')
    //   .pipe(takeUntil(this.unsubscriber))
    //   .subscribe(({ url }) => {
    //     this._blinkMemo(url);
    //   });

    merge(
      this._eventBusService.on(
        'UserDetailsDialogComponent:seeDetailsButtonClick'
      ),
      this._eventBusService.on(
        'CommentatorDetailsDialogComponent:seeDetailsButtonClick'
      ),
      this._eventBusService.on(
        'ReadingModeMemoComponent:seeDetailsButtonClick'
      ),
      this._eventBusService.on('ReadingGroupMemoListComponent:memoClick')
    )
      .pipe(takeUntil(this.unsubscriber))
      .subscribe((event: { url: string; source?: string }) => {
        // indicator 클릭으로 이미 _openBook이 호출된 경우 중복 호출 방지
        if (event.source === 'indicator') {
          return;
        }

        this._lastItemClickSource = event.source; // 소스 정보 저장
        const address = parseBukURL(event.url);

        if (!address.query) {
          address.query = {};
        }

        address.query.highlight = '#5EFFFF33';

        this._clearAnchorHighlight();
        this._openBook(address);
      });

    merge(
      this._eventBusService.on('MemoPopupComponent:closed'),
      this._eventBusService
        .on('RightPanelComponent:closed')
        .pipe(filter(({ activePanel }) => activePanel === 'myAnnotations'))
    )
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(() => {
        this._clearFocusedMemo();
      });

    merge(
      this._eventBusService.on('RightPanelComponent:opened'),
      this._eventBusService.on('RightPanelComponent:closed')
    )
      .pipe(takeUntil(this.unsubscriber))
      .subscribe((event) => {
        // 댓글창 열기/닫기로 인한 페이지 전환임을 표시
        if (event.activePanel === 'comment') {
          this._pageTransitionSource = 'panelToggle';
        }

        setTimeout(() => {
          this._updateMemoIndicator();
        }, 500);
      });

    // 본문 보기 취소 이벤트 구독
    this._eventBusService
      .on('ReadingModeMemoComponent:seeDetailsButtonCancel')
      .pipe(takeUntil(this.unsubscriber))
      .subscribe((data) => {
        if (!data || !data.url) {
          return;
        }

        const currentAddress = this._viewerComp?.getCurrentAddress()?.clone();

        if (!currentAddress) {
          return;
        }

        // 하이라이트 제거를 위해 highlight 쿼리 파라미터만 제거하고 현재 위치 유지
        if (currentAddress.query) {
          delete currentAddress.query.highlight;
        }

        // 본문보기 해제 시 source 설정
        this._lastItemClickSource = data.source || 'viewTextCancel';

        // 하이라이트 없이 현재 위치에서 다시 열기 (앵커 재계산 없이)
        this._openBook(currentAddress);

        // 포커스된 메모 클래스 제거
        this._clearFocusedMemo();

        // 인디케이터 재생성을 위해 약간의 지연 후 업데이트
        // 같은 페이지로 돌아오는 경우 _onItemLoad가 트리거되지 않을 수 있으므로 명시적으로 호출
        setTimeout(() => {
          this._updateMemoIndicator();
        }, 200);
      });

    // 나의책 본문 보기 취소 이벤트 구독
    this._eventBusService
      .on('AnnotationsComponent:clearHighlight')
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(() => {
        this._clearAnchorHighlight();
      });

    this._eventBusService
      .on('ItemMemoListComponent:groupClicked')
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(({ url, type }) => {
        this._focusMemo(url, type, true);
      });

    this._uiStateService.isDrawingMode$
      .pipe(takeUntil(this.unsubscriber))
      .subscribe((value) => {
        this._onDrawingModeChanged(value);
      });

    // 필기 표시 토글 시 오버레이 갱신
    this._uiStateService.showDrawing$
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(() => {
        this._onShowDrawingChanged();
      });
  }

  private _onShowDrawingChanged(): void {
    if (!this._address || !this._viewerComp) {
      return;
    }

    // 현재 주소를 강제로 다시 열어서 오버레이를 갱신
    const address = this._address.clone();
    address.query = Object.assign(address.query ?? {}, { force: true });
    this._viewerComp.openBookWithURL(address.toString());
  }

  private _onDrawingModeChanged(isDrawingMode: boolean): void {
    // 필기모드 전환 상태를 설정
    this._isDrawingModeTransition = true;

    if (isDrawingMode) {
      this._viewerComp?.setPageTapGestureEnabled(false);
    } else {
      this._viewerComp?.setPageTapGestureEnabled(true);
    }

    if (!this._address) {
      return;
    }

    const scrollZoom = this._element.nativeElement.querySelector(
      'bukv-pdf-page.bukv_is-active bukv-scroll-zoom'
    );
    const { scrollLeft, scrollTop } = scrollZoom;
    this._scrollZoomInfo = {
      scrollLeft,
      scrollTop,
    };
    (window as any).scrollZoom = {
      scrollLeft,
      scrollTop,
    };

    if (
      isDrawingMode &&
      this._viewerComp.getCurrentSettings().multiColumn.value
    ) {
      this._toastService.open(
        '필기 기능 이용을 위해 한 페이지 보기 모드로 변경되었습니다.'
      );
      this._onSettingsChanged('multiColumn', false);
    } else {
      const address = this._address.clone();
      address.query = Object.assign(address.query ?? {}, { force: true });
      this._viewerComp.openBookWithURL(address.toString());
    }

    if (!isDrawingMode) {
      this._pdfDrawingService?.saveDrawings().subscribe();
    }
  }

  ngAfterViewInit(): void {
    // detect URL change
    this._userService.user
      .pipe(takeUntil(this.unsubscriber), take(1), delay(0))
      .subscribe(() => {
        this._initWithURL(this._location.path());
      });

    this._router.events
      .pipe(
        scan(scanNavigationChangeResult, {}),
        takeUntil(this.unsubscriber),
        filter(
          (result) =>
            !!result.url &&
            canOpenBookWithURL(result.url) &&
            !result.isTriggeredByPopstate
        ),
        map((result) => result.url),
        delay(0)
      )
      .subscribe((url) => {
        this._initWithURL(url!);
      });

    this._locationChangeSubscription = this._location.subscribe(() => {
      const path = this._location.path();
      if (canOpenBookWithURL(path)) {
        this._initWithURL(path);
      }
    });
    /////////////////
  }

  ngOnDestroy(): void {
    this.unsubscriber.next();
    this.unsubscriber.complete();

    this._locationChangeSubscription.unsubscribe();

    this._destroyFrameBodyMutationObserver();

    document.body.removeAttribute('data-book-type');
  }

  _initWithURL(url: string): void {
    const address = parseBukURL(url);

    // 현재 주소와 동일하면 무시 (순환 방지)
    if (
      this._address &&
      this._address.bid === address.bid &&
      this._address.iid === address.iid &&
      this._address.anchor === address.anchor
    ) {
      return;
    }

    let gId: number | undefined;
    let cId: number | undefined;

    if (address.query?.gId) {
      const groupReaderDisabled =
        this._userService.getUser()?.group_reader_disabled;

      if (groupReaderDisabled) {
        const until = new Date(groupReaderDisabled);
        const since = new Date(groupReaderDisabled);
        since.setDate(until.getDate() + 1);

        this._toastService.openWarning(
          `신고 횟수 누적으로 ${until.getUTCFullYear()}.${
            until.getUTCMonth() + 1
          }.${until.getUTCDate()}일까지 혼자읽기 모드만 이용이 가능합니다. ${since.getUTCFullYear()}.${
            since.getUTCMonth() + 1
          }.${since.getUTCDate()}일부터 다시 함께읽기 모드를 이용할 수 있습니다.`
        );
      } else {
        gId = parseInt(address.query.gId);

        if (isNaN(gId)) {
          gId = undefined;
        }
      }
    }

    if (address.query?.cId) {
      cId = parseInt(address.query.cId);

      if (isNaN(cId) || gId != null) {
        cId = undefined;
      }
    }

    this._uiStateService.isCommentaryEditor =
      cId != null && address.query?.commentaryEditor != null;

    this._annotationsService.setState({
      groupId: gId,
      commentaryId: cId,
      showMine: !this._uiStateService.isCommentaryEditor,
    });

    const drawingLoading$ =
      this._pdfDrawingService?.loadDrawings(address.bid) ?? of(undefined);

    drawingLoading$
      .pipe(catchError<void, Observable<Error>>((error) => of(error)))
      .subscribe((error) => {
        if (error) {
          this._uiStateService.canUseDrawingMode = false;

          this._toastService.open(
            `필기를 불러오는데 실패하였습니다. 필기 기능 이용이 제한됩니다. (${error.message})`
          );
        } else {
          this._uiStateService.canUseDrawingMode = true;
        }

        // 여기서 /@ba1001 이런 주소인 경우는 뷰어로 열지 않음
        if (/^\/?@[^plwjv][a-z]{1,2}\d{4}$/.test(url)) {
          return;
        }

        this._viewerComp.openBookWithURL(url);
      });
  }

  _openBook(addressOrURL: Address | string): void {
    // let address;

    // if (typeof addressOrURL === 'string') {
    //   address = parseBukURL(addressOrURL);
    // } else {
    //   address = addressOrURL.clone();
    // }

    // if (!address.query) {
    //   address.query = {};
    // }

    // if (this._annotationsService.getState().commentaryId) {
    //   address.query!['cId'] = this._annotationsService.getState().commentaryId;
    //   delete address.query.gId;
    // } else {
    //   delete address.query.cId;
    // }

    // if (this._annotationsService.getState().groupId) {
    //   address.query!['gId'] = this._annotationsService.getState().groupId;
    //   delete address.query.cId;
    // } else {
    //   delete address.query.gId;
    // }

    // this._viewerComp.openBookWithURL(address.toString());
    if (typeof addressOrURL === 'string') {
      this._viewerComp.openBookWithURL(addressOrURL);
    } else {
      this._viewerComp.openBook(
        addressOrURL.bid,
        addressOrURL.iid,
        addressOrURL.anchor,
        addressOrURL.query
      );
    }
  }

  _syncAnnotations(): void {
    const annotations = this._annotationsService.getItemsAnnotations();
    const bookmarks = this._bookmarksService.bookmarks.map(
      convertBookmarkToViewerAnnotation
    );

    this._viewerComp?.setAnnotations(annotations.concat(bookmarks));
  }

  _onBookLoad(event: BookLoadEvent): void {
    this._book = new Book(event.book as BukJSON);

    if (this._uiStateService.isCommentaryEditor) {
      if (this._userService.getUser()?.isAdmin) {
        this._uiStateService.canEditCommentary = true;
      } else {
        this._uiStateService.canEditCommentary =
          this._book.meta.commentaries.find(
            (c) => c.id === this._annotationsService.getState().commentaryId
          )?.published_at === null;
      }
    } else {
      this._uiStateService.canEditCommentary = false;
    }

    this._referer = this._book.permissionReferer;

    this._destroyFrameBodyMutationObserver();
    this._isListeningZoomScrollEvent = false;

    //////
    const savedSettings = this._storageService.loadSettings();

    // 스크롤 모드를 페이지 모드로 강제 변경
    if (savedSettings && savedSettings.pagingMode === 'scroll') {
      (savedSettings as any).pagingMode = 'page';
    }

    this._shouldFixPagingMode = !this._book.isOwnedByUser;
    this._overrideSettings = settingValuesFromObject(
      this._viewerComp.getCurrentAddress()?.query ?? {}
    );

    if (this._shouldFixPagingMode) {
      this._overrideSettings.pagingMode = PagingMode.Page;
    }

    OVERRIDABLE_SETTING_KEYS.forEach((key) => {
      const overrideValue =
        this._overrideSettings && (this._overrideSettings[key] as any);
      const userValue = savedSettings && (savedSettings[key] as any);

      const value = overrideValue ?? userValue;
      value && this._viewerComp.updateSettings(key, value);
    });

    this._eventBusService.fire('ContentsComponent:settingsChanged', {
      settings: this._viewerComp.getCurrentSettings(),
      availableFonts: this._viewerComp.getFontsCurrentlyAvailable(),
      forceDisabledSettings: this._shouldFixPagingMode ? ['pagingMode'] : [],
    });
    ////////

    this._eventBusService.fire('ContentsComponent:bookLoad', {
      book: this._book,
    });

    this._canChangeAddress(this._viewerComp.getCurrentAddress()!);

    // 초기 접속 주소 저장 (비로그인 사용자를 위한)
    if (!this._book.permissionReferer && this._address) {
      this._initialAccessAddress = this._address.clone();
    } else {
      this._initialAccessAddress = null;
      this._initialAccessPageInfo = null;
    }
  }

  _onBookLoadError(event: BookLoadErrorEvent): void {
    console.error(event.raw);
    this._eventBusService.fire('ContentsComponent:bookLoadError', event);
  }

  _onAddressChange(event: AddressChangeEvent): void {
    // 뷰어 내에서만 쓰는 쿼리 삭제
    if (event.address.query) {
      delete event.address.query.force;
      delete event.address.query.highlight;
      delete event.address.query.expand;
      delete event.address.query.t;
      delete event.address.query.bookshelf;

      if (event.address.query.cPreview) {
        delete event.address.query.cPreview;
        delete event.address.query.memo;
        delete event.address.query.text;
        delete event.address.query.avatar;
        delete event.address.query.nickname;
      }

      delete event.address.query.mId;
      // delete event.address.query.commentaryEditor;
    }

    this._updateBrowserURL(event.address);

    this._address = event.address;
    this._eventBusService.fire('ContentsComponent:addressChange', event);
  }

  _onSettingsChange(event: SettingsChangeEvent): void {
    if (self === top) {
      this._storageService.saveSettings(
        event.settings,
        this._overrideSettings && (Object.keys(this._overrideSettings) as any)
      );
    }

    this._eventBusService.fire('ContentsComponent:settingsChanged', {
      settings: event.settings,
      availableFonts: this._viewerComp?.getFontsCurrentlyAvailable(),
      forceDisabledSettings: this._shouldFixPagingMode ? ['pagingMode'] : [],
    });
  }

  _onItemLoadStart(event: ItemLoadStartEvent): void {
    this._isItemLoading = true;

    // 기존 타이머가 있다면 정리
    if (this._loadTimeout) {
      clearTimeout(this._loadTimeout);
    }

    // 필기모드 전환 시 0.5초, 일반 페이지 전환 시 2초 이상 지속될 때만 로딩바 표시
    const delay = this._isDrawingModeTransition ? 500 : 1000;

    this._loadTimeout = setTimeout(() => {
      if (this._isItemLoading) {
        this._$.fire('loader.show.page');
      }
    }, delay);
  }

  _onItemLoad(event: ItemLoadEvent): void {
    this._isItemLoading = false;
    this._loadedItems = event.items;
    this._continuouslySelectedRange = undefined;

    this._$.fire('loader.hide.page');

    // 타이머 정리
    if (this._loadTimeout) {
      clearTimeout(this._loadTimeout);
      this._loadTimeout = null;
    }

    // 필기모드 전환 상태 초기화
    this._isDrawingModeTransition = false;

    // 댓글 → pending 하이라이트가 예약된 경우 이번 로드에서는 referer만 숨김 (새로고침 등 pending 없으면 referer 표시)
    this._suppressRefererHighlightForPendingComment =
      !!this._pendingHighlightAddress?.range;

    // 약간의 지연을 두어 DOM 렌더링 완료 대기
    setTimeout(() => {
      if (this._uiStateService.isRightPanelOpened) {
        this._highlightRefererRange();
      }

      this._highlightPendingAddress();
    }, 1000);

    const currWindowSize = { w: window.innerWidth, h: window.innerHeight };

    // 앱에서는 ResizeObserver가 처리하므로 이 로직 건너뛰기
    // 앱에서 이 로직을 실행하면 PDFDrawingService의 ResizeObserver와 충돌하여
    // 뷰어 크기가 계속 작아지는 문제가 발생함
    if (
      !this._isAppEnvironment &&
      this._book?.isSharedItem(event.address.iid || this._book.items[0].iid)
    ) {
      if (
        this._prevWindowSize &&
        (this._prevWindowSize.w !== currWindowSize.w ||
          this._prevWindowSize.h !== currWindowSize.h)
      ) {
        this._viewerComp.openBookWithURL(
          this._book.permissionReferer!.toString()
        );
      }
    }

    // 앱이 아닌 환경에서만 _prevWindowSize 업데이트
    if (!this._isAppEnvironment) {
      this._prevWindowSize = currWindowSize;
    }
    this._eventBusService.fire('ContentsComponent:itemLoad', event);

    setTimeout(() => {
      if (this._scrollZoomInfo) {
        const { scrollLeft, scrollTop } = this._scrollZoomInfo;
        const scrollZoom = this._element.nativeElement.querySelector(
          'bukv-pdf-page.bukv_is-active bukv-scroll-zoom'
        );
        scrollZoom.scrollLeft = scrollLeft;
        scrollZoom.scrollTop = scrollTop;
        this._scrollZoomInfo = null;
        (window as any).scrollZoom = null;
      }
      this._processContentDocument();
      this._updateMemoIndicator();

      this._memosAPIService
        .get(event.address.bid, 0, 999, {
          group_id: this._annotationsService.getState().groupId,
        })
        .subscribe((memos) => {
          const selectedMemo = memos.items.find(
            (memo) => memo.id === this._referer?.query?.mId
          );

          if (selectedMemo) {
            this._eventBusService.fire('MemoListComponent:commentButtonClick', {
              memo: selectedMemo,
              source: 'alert', // 소스 정보 추가
              cId: this._referer?.query?.cId,
            });
          }
        });
    });

    if (!this._isListeningZoomScrollEvent) {
      this._listenZoomScrollEvent();
    }

    document.body.style.willChange = 'auto';
    setTimeout(() => {
      document.body.style.willChange = 'transform';

      setTimeout(() => {
        document.body.style.willChange = 'auto';
      }, 100);
    }, 100);
  }

  private _listenZoomScrollEvent(): void {
    this._isListeningZoomScrollEvent = true;

    fromUserScrollEvent(
      this._element.nativeElement.querySelectorAll('bukv-scroll-zoom')
    )
      .pipe(throttleTime(300))
      .subscribe(() => {
        this._eventBusService.fire('ContentsComponent:pdfPageScrolled');
      });
  }

  _onItemLoadError(event: ItemLoadErrorEvent): void {
    this._isItemLoading = false;

    if (event.code === ItemLoadErrorCode.ItemNotFound) {
      this._eventBusService.fire('ContentsComponent:itemLoadError', event);
    }
  }

  _onPageTransitionEnd(): void {
    this._eventBusService.fire('ContentsComponent:pageTransitionEnd', {
      source: this._pageTransitionSource,
    });

    this._pageTransitionSource = undefined;
  }

  _onPageInfoChange(event: PageInfoChangeEvent): void {
    this._currentPage = {
      pageCount: event.pageCount,
      page: event.page,
    };

    const currentAddress = this._viewerComp.getCurrentAddress()!;
    const currentIid = currentAddress.iid || this._book?.items[0].iid;

    if (
      this._book?.isSharedItem(currentIid) &&
      this._book.permissionReferer!.anchor === currentAddress.anchor
    ) {
      this._sharedPageInfo = {
        pageCount: event.pageCount,
        page: event.page,
      };
    }

    // 비로그인 사용자를 위한 초기 접속 페이지 정보 저장
    if (
      !this._book?.permissionReferer &&
      this._initialAccessAddress &&
      this._initialAccessAddress.iid === currentIid &&
      this._initialAccessAddress.anchor === currentAddress.anchor
    ) {
      this._initialAccessPageInfo = {
        pageCount: event.pageCount,
        page: event.page,
      };
    }

    if (this._book!.meta.type !== BookType.Wiki) {
      this._loggerService.setBookMetrics(
        this._loadedItems.map((item) => ({
          bid: currentAddress.bid,
          iid: item.iid,
          page: event.page,
          pageCount: event.pageCount,
        }))
      );
    }

    this._currentTOCIndex = event.tocIndex;

    if (this._book?.canReadItem(currentIid, true)) {
      this._eventBusService.fire('ContentsComponent:pageInfoChange', {
        ...event,
        address: currentAddress,
        source: this._lastItemClickSource, // 소스 정보 전달
      });
      this._lastItemClickSource = undefined; // 사용 후 초기화
    }
  }

  _onBookmarkStateChange(event: BookmarkStateChangeEvent): void {
    this._eventBusService.fire('ContentsComponent:bookmarkStateChange', event);
  }

  _onPageTap(event: PageTapEvent): void {
    if (!event.isPageChangeGesture && !this._isContextMenuShowing) {
      this._eventBusService.fire('ContentsComponent:pageClick');
    } else {
      this._eventBusService.fire('ContentsComponent:pageClick2');
    }
  }

  private _onEndOfBook(direction: Direction): void {
    this._eventBusService.fire('ContentsComponent:endOfBook', {
      direction,
    });
  }

  _onHighlightClick(event: HighlightClickEvent): void {
    let userHighlight;

    if (this._uiStateService.isCommentaryEditor) {
      userHighlight = event.highlights.find(
        (h) => h.annotation.styleClass === MOMO_HIGHLIGHT_STYLE_CLASS_COMMENTARY
      );
    } else {
      // 그룹책 읽기 모드인지 확인
      const isGroupReadingMode =
        this._annotationsService.getState().groupId !== undefined;

      if (isGroupReadingMode) {
        // 그룹책 읽기 모드: 내 밑줄(bukh-color-reading-g)만 찾기
        userHighlight = event.highlights.find((h) => {
          if (h.annotation.styleClass === 'bukh-color-reading-g') {
            // 내 밑줄인지 확인
            const highlight =
              this._annotationsService.getHighlightFromViewerAnnotation(
                h.annotation
              );
            return highlight && highlight.is_mine;
          }
          return false;
        });
      } else {
        // 일반 모드: 기존 로직 유지
        userHighlight = event.highlights.find(
          (h) =>
            USER_HIGHLIGHT_STYLE_CLASSES.indexOf(h.annotation.styleClass!) !==
            -1
        );
      }
    }

    if (!userHighlight) {
      return;
    }

    const canPlayMediaOverlay = this._viewerComp.canPlayMediaOverlayFromRange(
      userHighlight.range
    );

    this._eventBusService.fire('ContentsComponent:highlightClick', {
      context: {
        url: userHighlight.annotation.url,
        selectedRange: userHighlight.range,
        annotation: userHighlight.annotation,
        canPlayMediaOverlay,
        isAtPageBoundary: false,
      },
      offset: { x: event.offsetX, y: event.offsetY },
    });
  }

  _onDeselectText(): void {
    this._eventBusService.fire('ContentsComponent:deselect');
  }

  _onTextSelectionLimit(): void {
    this._toastService.openWarning('문구 선택은 500자 이하로 제한됩니다.');
    this._viewerComp.clearSelection();
  }

  _onSelectText(event: SelectionChangeEvent): void {
    // 텍스트 선택 완료 시 임시 댓글 하이라이트 제거
    this._clearTemporaryCommentHighlight();
    this._clearAnchorHighlight();

    const url = event.selection!.url;

    if (!url) {
      return;
    }

    if (modifyRangeWithinViewport(event.selection!.range)) {
      return;
    }

    const annotation = this._viewerComp
      .getHighlightsFromRange(event.selection!.range)
      .find((h) => {
        return (
          h.annotation.styleClass &&
          USER_HIGHLIGHT_STYLE_CLASSES.indexOf(h.annotation.styleClass) !== -1
        );
      })?.annotation;

    const canPlayMediaOverlay = this._viewerComp.canPlayMediaOverlayFromRange(
      event.selection!.range
    );

    this._eventBusService.fire('ContentsComponent:select', {
      rects: event.selection!.rects,
      context: {
        url,
        selectedRange: event.selection!.range,
        canPlayMediaOverlay,
        annotation,
        isAtPageBoundary: isRangeAtPageBoundary(event.selection!.range),
      },
    });
  }

  _onAnnotationCreated(event: AnnotationCreatedEvent): void {
    if (event.annotation.type === AnnotationType.Highlight) {
      if (this._uiStateService.isCommentaryEditor) {
        event.annotation.styleClass = HighlightColor.Yellow;
      }

      this._annotationsService.createHighlight(event.annotation).subscribe({
        next: () => {
          this._annotationsService
            .deleteHighlights(event.mergedAnnotations)
            .subscribe(() => {
              this._eventBusService.fire('ContentsComponent:highlightUpdated');
            });
          this._doPendingAnnotAction(event.annotation);
        },
        error: (error: Error | APIError) => {
          event.annotation.type === AnnotationType.Highlight &&
            this._viewerComp?.removeHighlight(
              event.annotation.url,
              event.annotation.styleClass!,
              { emitEvent: false }
            );
          this._onCreateAnnotationError(error);
          this._doPendingAnnotAction(event.annotation, error);
        },
      });
    } else {
      if (this._book!.meta.type === BookType.PDF) {
        const address = parseBukURL(event.annotation.url);
        address.anchor = createPermissionAnchor();
        event.annotation.url = address.toString();
      }

      if (event.annotation.text.trim().length === 0) {
        event.annotation.text =
          this._book!.toc[this._currentTOCIndex]?.title ?? '';
      }

      event.annotation.text = event.annotation.text.substr(0, 100);

      this._bookmarksService.create(event.annotation).subscribe();
    }
  }

  _onAnnotationChanged(event: AnnotationChangedEvent): void {
    this._annotationsService
      .updateHighlight(event.annotation, event.annotation.styleClass!)
      .subscribe({
        next: () => {
          this._eventBusService.fire('ContentsComponent:highlightUpdated');
        },
        error: (error: APIError) => {
          this._toastService.openWarning(error.message);
        },
      });
  }

  _onAnnotationRemoved(event: AnnotationRemovedEvent): void {
    // FIXME
    if (event.annotations[0].type === AnnotationType.Highlight) {
      this._annotationsService
        .deleteHighlights(event.annotations)
        .subscribe(() => {
          this._eventBusService.fire('ContentsComponent:highlightUpdated');
        });
    } else {
      this._bookmarksService.remove(event.annotations).subscribe();
    }
  }

  _onPageChangeCanceled(event: PageChangeCanceledEvent): void {
    switch (event.code) {
      case PageChangeCancelCode.NoNextItem:
        this._onEndOfBook(event.direction);
        break;
    }
  }

  _onZoomScaleChange(event: ZoomScaleChangeEvent): void {
    this._currentZoomScale = event.scale;
    this._eventBusService.fire('FixedLayoutPageControlComponent:changeZoom', {
      scale: event.scale,
    });
  }

  private _onOutOfPreviewBounds(address: Address): void {
    if (!this._book) {
      return;
    }

    const openedDialog =
      this._alertService.openDialogs[this._alertService.openDialogs.length - 1];

    if (openedDialog?.componentInstance instanceof PreviewEndDialogComponent) {
      return;
    }

    if (!this._loadedItems) {
      const readableItem = this._book.items.find((item) =>
        this._book?.canReadItem(item.iid)
      );

      if (readableItem) {
        this._openBook(new Address(address.bid, readableItem.iid));
      }
    }

    this._alertService.open(PreviewEndDialogComponent, {
      data: { book: this._book },
    });
  }

  private _canChangeAddress(newAddress: Address): boolean {
    if (this._book?.meta.bid !== newAddress.bid) {
      return true;
    }

    const currIid = newAddress.iid || this._book.items[0].iid;

    if (
      this._book.canReadItem(currIid, this._book.meta.type === BookType.PDF)
    ) {
      return true;
    }

    // 로그인 사용자: isSharedItem 체크
    if (this._book.isSharedItem(currIid)) {
      const item = this._book.getItem(newAddress.iid);

      if (item && !item.url) {
        this._onOutOfPreviewBounds(newAddress);
      }

      if (newAddress.range) {
        return this._book.permissionReferer?.anchor === newAddress.range;
      }

      if (!this._sharedPageInfo || newAddress.fragment) {
        this._onOutOfPreviewBounds(newAddress);
        return false;
      }

      const page = Math.min(
        Math.floor((newAddress.page || 0) * this._sharedPageInfo.pageCount) + 1,
        this._sharedPageInfo.pageCount
      );

      let additionalPage = 1;

      if (window.innerWidth < 415) {
        additionalPage *= 2;
      }

      if (page > this._sharedPageInfo.page + additionalPage) {
        this._onOutOfPreviewBounds(newAddress);
        return false;
      }

      if (page < this._sharedPageInfo.page - additionalPage) {
        this._onOutOfPreviewBounds(newAddress);
        return false;
      }

      return true;
    }

    // 비로그인 사용자: 초기 접속 주소 기반으로 앞뒤 페이지 허용
    if (!this._book.permissionReferer && this._initialAccessAddress) {
      const initialIid =
        this._initialAccessAddress.iid || this._book.items[0].iid;

      // 초기 접속한 아이템과 다른 아이템으로 이동하려는 경우
      if (currIid !== initialIid) {
        this._onOutOfPreviewBounds(newAddress);
        return false;
      }

      // 초기 접속한 아이템 내에서 페이지 이동
      if (!this._initialAccessPageInfo || newAddress.fragment) {
        // 초기 접속 페이지 정보가 없으면 현재 페이지만 허용
        if (
          newAddress.page !== undefined &&
          newAddress.page !== this._initialAccessAddress.page
        ) {
          this._onOutOfPreviewBounds(newAddress);
          return false;
        }
        return true;
      }

      const page = Math.min(
        Math.floor(
          (newAddress.page || 0) * this._initialAccessPageInfo.pageCount
        ) + 1,
        this._initialAccessPageInfo.pageCount
      );

      let additionalPage = 1;

      if (window.innerWidth < 415) {
        additionalPage *= 2;
      }

      if (page > this._initialAccessPageInfo.page + additionalPage) {
        this._onOutOfPreviewBounds(newAddress);
        return false;
      }

      if (page < this._initialAccessPageInfo.page - additionalPage) {
        this._onOutOfPreviewBounds(newAddress);
        return false;
      }

      return true;
    }

    // 그 외의 경우 (비로그인이고 초기 접속 주소도 없는 경우)
    this._onOutOfPreviewBounds(newAddress);

    if (isSharedAddress(newAddress) && this._book.permission.code === 405) {
      this._toastService.openWarning(
        '저작권자의 요청으로 본 챕터는 내용의 공유가 제한됩니다.'
      );
    }

    return false;
  }

  private _onCreateAnnotationError(error: Error | APIError): void {
    if (error instanceof APIError) {
      this._toastService.openWarning(error.message);
    } else {
      if (error.name === MAX_ANNOTATION_LENGTH_EXCEEDED_ERROR) {
        this._onTextSelectionLimit();
      } else {
        this._toastService.openWarning(
          '오류가 발생하였습니다. 잠시 후 다시 시도해 주세요.'
        );
      }
    }

    this._syncAnnotations();
  }

  private _doPendingAnnotAction(
    annotation: Annotation,
    error?: Error | APIError
  ): void {
    this._pendingAnnotActions.get(annotation.url)?.(annotation, error);
    this._pendingAnnotActions.delete(annotation.url);
  }

  private _onShowContextMenu(): void {
    window.clearTimeout(this._hideContextMenuTimeoutId);
    this._isContextMenuShowing = true;
  }

  private _onHideContextMenu(): void {
    this._ngZone.runOutsideAngular(() => {
      this._hideContextMenuTimeoutId = window.setTimeout(() => {
        this._isContextMenuShowing = false;
      }, 500);
    });
  }

  private _createHighlight(
    fromAction: 'highlight' | 'share' | 'memo',
    range: Range | string,
    styleClass?: string,
    emitEvent: boolean = true
  ): { annotation: Annotation; mergedAnnotations: Annotation[] } | null {
    let canHighlight = true;

    const isLoggedIn = !!this._userService.getUser();

    if (!isLoggedIn) {
      this._toastService
        .open('로그인 후 이용할 수 있습니다.', '로그인하기')
        .onAction()
        .subscribe(() => {
          this._authService.login(location.href);
        });
      canHighlight = false;
    } else if (!this._book?.isOwnedByUser) {
      this._toastService.open('해당 기능 사용을 위해서는 구매가 필요합니다.');
      canHighlight = false;
    }

    if (!canHighlight) {
      this._viewerComp?.clearSelection();
      this._eventBusService.fire('ContentsComponent:deselect');
      return null;
    }

    if (!styleClass) {
      styleClass = this._libConfig.highlightStyles![0].styleClass;
    }

    // 병합 대상 스타일 클래스 결정
    let mergeWith: string[] = [];
    if (fromAction !== 'memo') {
      if (styleClass === 'bukh-color-reading-g') {
        // 밑줄인 경우: 같은 밑줄 스타일과 병합
        mergeWith = ['bukh-color-reading-g'];
      } else {
        // 하이라이트인 경우: 하이라이트 스타일과 병합
        mergeWith = USER_HIGHLIGHT_STYLE_CLASSES;
      }
    }

    return this._viewerComp.createHighlight(range, styleClass, {
      expandToWord: false,
      mergeWith,
      emitEvent,
    });
  }

  private _onHighlightMenuClick(
    action: HighlightAction,
    context: SelectionContext,
    styleClass?: string
  ): void {
    if (this._uiStateService.isCommentaryEditor) {
      if (!this._uiStateService.canEditCommentary) {
        this._toastService.openWarning(
          '판매 설정이 완료된 코멘터리는 수정이 불가합니다.'
        );

        return;
      }

      styleClass = MOMO_HIGHLIGHT_STYLE_CLASS_COMMENTARY;
    } else if (!this._annotationsService.getState().showMine) {
      this._toastService.openWarning(
        '내 기록 보기 옵션이 꺼져있습니다. 먼저 옵션을 켜주세요.'
      );
      this._eventBusService.fire(
        'ContentsComponent:contextMenuClickWithoutRequiredSettings'
      );
      return;
    }

    switch (action) {
      case HighlightAction.Add:
        this._createHighlight('highlight', context.selectedRange, styleClass);
        break;
      case HighlightAction.Change:
        this._viewerComp.changeHighlight(
          context.annotation!.url,
          context.annotation!.styleClass!,
          styleClass!
        );
        break;
      case HighlightAction.Remove:
        this._viewerComp.removeHighlight(
          context.annotation!.url,
          context.annotation!.styleClass!
        );
        break;
    }
  }

  private _onContextMenuClick(
    menu: ContextMenuItem,
    context: SelectionContext
  ): void {
    if (
      this._uiStateService.isCommentaryEditor &&
      !this._uiStateService.canEditCommentary
    ) {
      this._toastService.openWarning(
        '판매 설정이 완료된 코멘터리는 수정이 불가합니다.'
      );

      return;
    }

    if (
      menu === ContextMenuItem.CopyText ||
      menu === ContextMenuItem.CopyURL ||
      menu === ContextMenuItem.ShareFacebook ||
      menu === ContextMenuItem.ShareTwitter ||
      menu === ContextMenuItem.Memo
    ) {
      if (
        !this._uiStateService.isCommentaryEditor &&
        !this._annotationsService.getState().showMine
      ) {
        this._toastService.openWarning(
          `내 기록 보기 옵션이 꺼져있습니다. 먼저 옵션을 켜주세요.`
        );
        this._eventBusService.fire(
          'ContentsComponent:contextMenuClickWithoutRequiredSettings'
        );
        return;
      }
    }

    switch (menu) {
      case ContextMenuItem.CopyText:
        this._copySelectionText(context);
        break;
      case ContextMenuItem.CopyURL:
        this._copySelectionURL(context);
        break;
      case ContextMenuItem.ShareTwitter:
        this._shareSelection(context, 'twitter');
        break;
      case ContextMenuItem.ShareFacebook:
        this._shareSelection(context, 'facebook');
        break;
      case ContextMenuItem.MediaOverlay:
        this._viewerComp.playMediaOverlayFromRange(context.selectedRange);
        break;
      case ContextMenuItem.Memo:
        this._openMemoDialog(context);
        break;
      case ContextMenuItem.Comment:
        this._activateCommentInput(context);
        break;
      case ContextMenuItem.SelectContinuously:
        this._extendSelection(context.selectedRange);
        break;
    }

    this._analyticsService.trackEvent(
      'context-menu',
      getGATrackingLabelOfContextMenuItem(menu)
    );
  }

  private _extendSelection(range: Range): void {
    this._viewerComp.nextPage();

    setTimeout(() => {
      const doc = range.endContainer.ownerDocument!;
      const docSelection = doc.getSelection();

      if (!docSelection) {
        return;
      }

      docSelection.addRange(range);

      extendSelectionToWord(docSelection, 2);

      if (CrossBrowsing.noTextSelectionHandle) {
        this._continuouslySelectedRange = docSelection
          .getRangeAt(0)
          .cloneRange();
      }
    }, 500);
  }

  private _openMemoDialog(context: SelectionContext): void {
    // TODO: 같은 범위에 하는 메모 컨트롤 필요?

    const highlightResult = this._createHighlight(
      'memo',
      context.selectedRange,
      `buk-memo-temp-${Math.random()}`,
      false
    );

    if (highlightResult) {
      this._dialogService
        .open(MemoDialogComponent, {
          data: {
            data: {
              annotation: highlightResult.annotation,
              mergedAnnotations: highlightResult.mergedAnnotations,
            },
            type: 'memoCreated',
          },
        })
        .afterClosed()
        .subscribe((result) => {
          if (!result) {
            return;
          }
          if (result.canceled) {
            this._viewerComp?.removeHighlight(
              highlightResult.annotation.url,
              highlightResult.annotation.styleClass!,
              { emitEvent: false }
            );
          } else {
            this._eventBusService.fire('ContentsComponent:memoUpdated');
          }
        });
    }
  }

  // 댓글 입력창 활성화
  private _activateCommentInput(context: SelectionContext): void {
    // 댓글 달기 전에 bukh-anchor 하이라이트 삭제
    this._clearAnchorHighlight();

    const panelClosed = !this._uiStateService.isRightPanelOpened;
    const isPageMode =
      this._viewerComp.getCurrentSettings().pagingMode.value ===
      PagingMode.Page;

    // 패널이 닫혀 있고 페이지 모드에서 _openBook으로 전환될 때는
    // 곧 iframe이 다시 로드되므로 여기서는 하이라이트를 만들지 않고,
    // _onItemLoad → _highlightPendingAddress에서만 buk-temp-comment-highlight 적용
    if (panelClosed && this._address && isPageMode) {
      const addressFromRange = this._viewerComp.getAddressFromRange(
        context.selectedRange
      );

      if (addressFromRange?.range) {
        const newAddress = addressFromRange.clone();
        if (this._address.query) {
          newAddress.query = {
            ...newAddress.query,
            ...this._address.query,
          };
        }

        const annotation: Annotation = {
          type: AnnotationType.Highlight,
          url: newAddress.toString(),
          text: context.selectedRange.toString().trim(),
          styleClass: 'buk-temp-comment-highlight',
        };

        this._eventBusService.fire('ContentsComponent:commentClick', {
          annotation,
        });

        this._openBook(newAddress);
        this._pendingHighlightAddress = newAddress;
        this._suppressRefererHighlightForPendingComment = true;

        this._eventBusService.fire('HeaderComponent:menuClick', {
          menu: HeaderMenuItem.GroupMemos,
        });

        return;
      }
    }

    const highlightResult = this._createHighlight(
      'memo',
      context.selectedRange,
      `buk-temp-comment-highlight`,
      false
    );

    if (!highlightResult) {
      return;
    }

    // commentClick 이벤트를 먼저 발생시켜 _isCommentClickPending 플래그를 설정
    // 이렇게 하면 addressChange 이벤트에서 댓글 입력창이 비활성화되지 않음
    this._eventBusService.fire('ContentsComponent:commentClick', {
      annotation: highlightResult.annotation!,
    });

    // 오른쪽 패널이 열려있지 않은 경우 anchor 설정
    if (!this._uiStateService.isRightPanelOpened && this._address) {
      const annotationAddress = parseBukURL(highlightResult.annotation.url);

      if (annotationAddress.range) {
        const newAddress = annotationAddress.clone();
        // 현재 주소의 query 파라미터 유지
        if (this._address.query) {
          newAddress.query = {
            ...newAddress.query,
            ...this._address.query,
          };
        }
        // this._updateBrowserURL(newAddress);

        // 스크롤 모드가 아닐때만 openBook 호출
        if (
          this._viewerComp.getCurrentSettings().pagingMode.value ===
          PagingMode.Page
        ) {
          this._openBook(newAddress);
        } else {
          this._scrollRequired = true;
        }

        // 하이라이트할 주소를 저장해두고, _onItemLoad에서 적용
        this._pendingHighlightAddress = newAddress;
        this._suppressRefererHighlightForPendingComment = true;
      }
    }

    this._eventBusService.fire('HeaderComponent:menuClick', {
      menu: HeaderMenuItem.GroupMemos,
    });
  }

  private _makeShareURL(annotURL: string): string {
    const user = this._userService.getUser();

    if (!user) {
      throw new Error('Cannot make share url without user');
    }

    return createSharingURL(parseBukURL(annotURL), user);
  }

  private _copySelectionText(context: SelectionContext): void {
    const rangeIID = getIIDFromRange(context.selectedRange);
    const canShareItem = this._book!.canShareItem(rangeIID);

    if (!canShareItem) {
      this._toastService.open(
        '저작권자의 요청으로 본 챕터는 내용의 복사가 제한됩니다.'
      );
      return;
    }

    const isLoggedIn = !!this._userService.getUser();

    if (!isLoggedIn) {
      this._clipboard.copy(context.selectedRange.toString().trim());
      this._toastService.open('문구가 복사되었습니다.');

      return;
    }

    const copyAnnotationText = (annot: Annotation): void => {
      const text = annot.text.trim();

      this._clipboard.copy(text);
      this._toastService.open('문구가 복사되었습니다.');
    };

    const copyToClipboardManually = (annot: Annotation): void => {
      const text = annot.text.trim();

      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);

      textarea.select();
      const success = document.execCommand('copy');
      document.body.removeChild(textarea);

      if (success) {
        this._toastService.open('문구가 복사되었습니다.');
      }
    };

    if (context.annotation) {
      copyAnnotationText(context.annotation);
    } else {
      const annotation = this._createHighlight(
        'share',
        context.selectedRange
      )?.annotation;

      if (!annotation) {
        // show error
        return;
      }

      copyToClipboardManually(annotation);

      // this._pendingAnnotActions.set(annotation.url, (_, error) => {
      //   if (!error) {
      //     copyAnnotationText(annotation);
      //   }
      // });
    }
  }

  private _copySelectionURL(context: SelectionContext): void {
    const rangeIID = getIIDFromRange(context.selectedRange);
    const canShareItem = this._book!.canShareItem(rangeIID);

    if (!canShareItem) {
      this._toastService.open(
        '저작권자의 요청으로 본 챕터는 내용의 공유가 제한됩니다.'
      );
      return;
    }

    // 선택된 텍스트 가져오기
    const selectedText = context.selectedRange.toString().trim();
    // 책 제목 가져오기
    const bookTitle = this._book!.meta.title;

    const copyAnnotationURL = (annot: Annotation): void => {
      const shareURL = this._makeShareURL(annot.url);

      // 텍스트, 책 제목, URL을 함께 복사
      const copyText = `${selectedText} [${bookTitle}]\n${shareURL}`;

      this._clipboard.copy(copyText);
      this._toastService.open('본문과 공유 링크가 복사되었습니다.');
    };

    if (context.annotation) {
      copyAnnotationURL(context.annotation);
    } else {
      // 하이라이트 생성 없이 범위에서 URL 생성
      // 임시로 하이라이트를 생성하여 URL을 가져온 후 즉시 삭제
      const highlightResult = this._viewerComp.createHighlight(
        context.selectedRange,
        this._libConfig.highlightStyles![0].styleClass,
        {
          mergeWith: [],
          emitEvent: false, // 이벤트 발생 방지
        }
      );

      if (highlightResult?.annotation) {
        const shareURL = this._makeShareURL(highlightResult.annotation.url);
        const copyText = `${selectedText} [${bookTitle}]\n${shareURL}`;

        // 하이라이트 즉시 삭제
        this._viewerComp.removeHighlight(
          highlightResult.annotation.url,
          highlightResult.annotation.styleClass!,
          { emitEvent: false }
        );

        this._clipboard.copy(copyText);
        this._toastService.open('본문과 공유 링크가 복사되었습니다.');
      } else {
        // 하이라이트 생성 실패 시 텍스트와 책 제목만 복사
        const copyText = `${selectedText} [${bookTitle}]`;
        this._clipboard.copy(copyText);
        this._toastService.open('문구가 복사되었습니다.');
      }
    }
  }

  private _shareSelection(
    context: SelectionContext,
    sns: 'twitter' | 'facebook'
  ): void {
    const rangeIID = getIIDFromRange(context.selectedRange);
    const canShareItem = this._book!.canShareItem(rangeIID);

    if (!canShareItem) {
      this._toastService.open(
        '저작권자의 요청으로 본 챕터는 내용의 공유가 제한됩니다.'
      );
      return;
    }

    if (context.annotation) {
      window.open(
        createSNSShareUrl(
          this._makeShareURL(context.annotation.url),
          context.annotation.text,
          sns
        ),
        undefined,
        createSNSShareWindowFeatures()
      );
    } else {
      const annotation = this._createHighlight(
        'share',
        context.selectedRange
      )?.annotation;

      if (!annotation) {
        // show error
        return;
      }

      const shareWindow = window.open(
        undefined,
        undefined,
        createSNSShareWindowFeatures()
      );

      shareWindow?.document.write('Loading...');

      this._pendingAnnotActions.set(annotation.url, (_, error) => {
        if (!error) {
          shareWindow?.location.assign(
            createSNSShareUrl(
              this._makeShareURL(annotation.url),
              annotation.text,
              sns
            )
          );
        } else {
          shareWindow?.close();
        }
      });
    }
  }

  private _onKeydown(event: KeyboardEvent): void {
    if (isShortcutKeyboardEvent(event) && event.key === 'f') {
      event.preventDefault();
      this._onSearchShortcut();
    }

    // epub인 경우 분기처리
    if (this._book?.meta.type !== BookType.PDF) {
      return;
    }

    // 줌 단축키: - 25% 축소, = 25% 확대 (입력 요소에서는 무시)
    const target = event.target as HTMLElement;
    const isEditable =
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.isContentEditable;

    if (!isEditable && this._viewerComp) {
      const ZOOM_STEP = 25;
      const MIN_ZOOM = 100;
      const MAX_ZOOM = 300;

      if (event.key === '-') {
        event.preventDefault();
        const newScale = clamp(
          this._currentZoomScale - ZOOM_STEP,
          MIN_ZOOM,
          MAX_ZOOM
        );

        this._currentZoomScale = newScale;
        this._eventBusService.fire(
          'FixedLayoutPageControlComponent:changeZoom',
          { scale: newScale }
        );
      } else if (event.key === '=') {
        event.preventDefault();
        const newScale = clamp(
          this._currentZoomScale + ZOOM_STEP,
          MIN_ZOOM,
          MAX_ZOOM
        );
        this._currentZoomScale = newScale;

        this._eventBusService.fire(
          'FixedLayoutPageControlComponent:changeZoom',
          { scale: newScale }
        );
      }
    }
  }

  private _onSearchShortcut(): void {
    if (!this._book) {
      return;
    }

    if (!this._bookFeaturesStoreService.features.text_search) {
      return;
    }

    this._dialogService.open(SearchDialogComponent, {
      data: {
        book: this._book,
      },
    });
  }

  private _processContentDocument(): void {
    getActiveIframes(this._element.nativeElement).forEach((iframe) => {
      if (iframe.contentDocument!.documentElement.dataset.bukInit == null) {
        fromKeydownEvent(iframe.contentDocument!.documentElement).subscribe(
          (event) => {
            this._onKeydown(event);
          }
        );

        let continuouslySelectedRange: Range | undefined;

        // 윈도우즈 크롬에서 텍스트 선택 위에서 드래그 시 텍스트 선택이 안되는 문제 수정
        iframe.contentDocument?.addEventListener('dragstart', (event) => {
          if (this._continuouslySelectedRange) {
            event.preventDefault();
          }
        });

        iframe.contentDocument?.addEventListener('selectstart', () => {
          if (this._continuouslySelectedRange) {
            continuouslySelectedRange = this._continuouslySelectedRange;
            this._continuouslySelectedRange = undefined;
          }
        });

        iframe.contentDocument?.addEventListener('selectionchange', () => {
          if (!continuouslySelectedRange) {
            return;
          }

          const selection = iframe.contentDocument?.getSelection();

          if (selection && selection.rangeCount > 0) {
            const currentRange = selection.getRangeAt(0);

            try {
              if (
                currentRange.compareBoundaryPoints(
                  Range.END_TO_START,
                  continuouslySelectedRange
                ) < 1
              ) {
                selection.setBaseAndExtent(
                  continuouslySelectedRange.startContainer,
                  continuouslySelectedRange.startOffset,
                  currentRange.endContainer,
                  currentRange.endOffset
                );
              }
            } catch (error) {
              console.warn(error);
            }
          }

          continuouslySelectedRange = undefined;
        });

        iframe.contentDocument!.addEventListener('copy', onCopyContentDocument);

        // 트랙패드 핀치 줌 disable
        iframe.contentDocument!.documentElement.addEventListener(
          'wheel',
          (event) => {
            event.ctrlKey && event.preventDefault();
          },
          { passive: false }
        );

        iframe.contentDocument!.head.insertAdjacentHTML(
          'beforeend',
          `<style>
          x.bukh-color-shared {
            cursor: default !important;
          }

          x {
            position: relative;
          }

          ::selection {
            background-color: #5EFFFF33;
          }
          ::-moz-selection {
            background-color: #5EFFFF33;
          }
          ::-webkit-selection {
            background-color: #5EFFFF33;
          }

          .${MEMO_INDICATOR_CLASS} {
            top: 2px;
            left: 1px;

            display: inline;
            position: absolute;

            background: #5EFFFF;
            border-radius: 50%;
            overflow: visible;

            cursor: pointer;

            &.my-book {
              top: 0px;
              left: 0px;
              width: 14px !important;
              height: 14px !important;
              background: transparent;
              background-image:url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABwAAAAcCAYAAAByDd+UAAAACXBIWXMAABYlAAAWJQFJUiTwAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAH0SURBVHgBvZa/T8JAFMdfa4Md+B0HxjrByOzUxKWTcXAgcSFxcXLzH/AvcNcYEh2IMhAmFhImEzfc2MRFiQ5WwQRIAO8LB1Zs6VEK3+S10DvuQ9+7d+9JJKCnIWnslmKGe5SZyoc6zJrc6tsSNdzWklxAaXbTOUREJrMqA9doESADAZBhliBvAjjHwKYrkL+VQb9u8yq4u8igdUcggyFOGfJXRauLZQsMbtwn/2Xwtf8CmbK0vBvthDWnXlNw4XH7txNvL7aSd5cxw23FcKxv7uy2a0enb48OUxJgwLUKf6DbzWqZstr+2nBNCcwpXMW179aGenL2+uAwTWdWk3lSi+bZXFVKYf29qTiFJQoWYpgin9TrSmrpOp6cMyUFoNfk9iJNEQUGNoedvcOPst1Y6SZm9HqSyA6PAiiUCnBXuRDRbcfEYJCq0BLqdWR1AdhIAOLMc/0RXGocfFatz+4rwfTLc0AjcXUAxInuGke4lOXaskdfE7u0QevTCFin9aku87bApNXLBGuyS6tkU5pCkUE3GO4v9GdC4UHXYaiKy7QAs3PumFZ36uDtzvHBWg/zNE4Rv4U1c5MvUyBveMrkv4rWZsquiUL1QDz9aKLKsy3jvDYxS97rJBrjvFCbOANeTyNsA9ZoXKgT3KytPiANEmz1fwCjG5k/WdzTfAAAAABJRU5ErkJggg==');
              background-repeat: no-repeat;
              background-position: center;
              background-size: 14px 14px;
              }
          }

          .${MEMO_INDICATOR_CLASS}[data-type="${MEMO_INDICATOR_TYPE_MINE}"] {
            width: var(--memo-indicator-size, 10px);
            height: var(--memo-indicator-size, 10px);

            transform: translate(-7px, -8px);
          }

          .${MEMO_INDICATOR_CLASS}[data-type="${MEMO_INDICATOR_TYPE_SHARED}"] {
            width: var(--memo-indicator-size, 10px);
            height: var(--memo-indicator-size, 10px);
            transform: translate(-7px, -8px);
          }

          .${MEMO_INDICATOR_CLASS}[data-type="${MEMO_INDICATOR_TYPE_MINE_SHARED}"] {
            width: var(--memo-indicator-size, 10px);
            height: var(--memo-indicator-size, 10px);
            transform: translate(-14px, -8px);
          }

          .${MEMO_INDICATOR_CLASS}[data-type="${MEMO_INDICATOR_TYPE_COMMENTARY}"] {
            width: var(--memo-indicator-size, 10px);
            height: var(--memo-indicator-size, 10px);
            transform: translate(-7px, -8px);
          }

          .${MEMO_INDICATOR_CLASS}[data-type="${MEMO_INDICATOR_TYPE_MINE_COMMENTARY}"] {
            width: var(--memo-indicator-size, 10px);
            height: var(--memo-indicator-size, 10px);
            transform: translate(-14px, -8px);
          }

          .${FOCUSED_MEMO_CLASS_SINGLE} {
            background: rgba(224, 255, 0, 0.2);
          }

          .${FOCUSED_MEMO_CLASS_DOUBLE} {
            background: rgba(224, 255, 0, 0.4);
          }

          @keyframes blink{
            0% {background: transparent;}
            100% {background: rgba(203, 308, 132, 0.58);}
          }

          .bukh-color-momo-m.animation {
            animation: blink 0.2s ease-in-out 4 alternate;
          }

          x.bukh-color-momo-overlap2,
          x.bukh-color-momo-overlap5,
          x.bukh-color-momo-overlap10 {
            cursor: auto;
          }

          x.bukh-color-reading-g x.bukh-color-momo-overlap2,
          x.bukh-color-reading-g x.bukh-color-momo-overlap5,
          x.bukh-color-reading-g x.bukh-color-momo-overlap10 {
            cursor: pointer;
          }

          x.buk-temp-comment-highlight {
            background-color: #5EFFFF33 !important;
            padding: 2px 0;
          }
        </style>`
        );

        // 다국어 책: 선택된 언어만 보이도록 (iframe 내부에 이미 ko, en 등 클래스가 붙어 있음)
        const locales = this._book?.meta?.locales;
        if (locales?.length) {
          const multiLangStyle = locales
            .map(([langCode]) => langCode)
            .map(
              (langCode) =>
                `body:not(.${langCode}) [lang="${langCode}"]{ display: none; }`
            )
            .join('\n');
          iframe.contentDocument!.head.insertAdjacentHTML(
            'beforeend',
            `<style data-bukv-multi-lang>${multiLangStyle}</style>`
          );
        }

        // 부모 창 크기에 따라 CSS 변수 동적 설정
        const updateMemoIndicatorSize = (): void => {
          const parentWidth = window.innerWidth;
          const indicatorSize = parentWidth <= 1200 ? '8px' : '10px';

          if (!iframe.contentDocument) {
            return;
          }

          iframe.contentDocument!.documentElement.style.setProperty(
            '--memo-indicator-size',
            indicatorSize
          );
        };

        // 초기 설정
        updateMemoIndicatorSize();

        // 리사이즈 이벤트 리스너 추가
        window.addEventListener('resize', updateMemoIndicatorSize);
      }

      // pdf text load 이벤트가 없어서 body 감시함
      if (
        iframe.contentDocument?.body &&
        iframe.contentDocument.body.innerHTML.length === 0
      ) {
        if (!this._frameBodyMutationObserver) {
          this._frameBodyMutationObserver = new MutationObserver(() => {
            this._onPDFTextLoadAfterItemLoad();
            this._destroyFrameBodyMutationObserver();
          });
        }

        this._frameBodyMutationObserver.observe(iframe.contentDocument.body, {
          childList: true,
        });
      } else {
        this._destroyFrameBodyMutationObserver();
      }

      iframe.contentDocument!.documentElement.dataset.bukInit = '';
    });
  }

  public _updateMemoIndicator(): void {
    const updateMemoIndicator = (
      iframe: HTMLIFrameElement,
      appendToBody: boolean
    ): void => {
      if (!iframe.contentDocument) {
        return;
      }

      const baseBukURL = iframe.contentDocument!.documentElement.getAttribute(
        'data-bukv-base-buk-url'
      );

      if (!baseBukURL) {
        return;
      }

      const indicators = iframe.contentDocument!.querySelectorAll(
        '.' + MEMO_INDICATOR_CLASS
      );

      indicators.forEach((indicator) => {
        indicator.remove();
      });

      const iid = parseBukURL(baseBukURL).iid;

      const isSingleMode =
        this._annotationsService.getState().groupId === undefined;

      const typeMixed = isSingleMode
        ? MEMO_INDICATOR_TYPE_MINE_COMMENTARY
        : MEMO_INDICATOR_TYPE_MINE_SHARED;
      const typeOthers = isSingleMode
        ? MEMO_INDICATOR_TYPE_COMMENTARY
        : MEMO_INDICATOR_TYPE_SHARED;

      const memoGroupInfoMap = this._annotationsService
        .getMemosOfItem(iid!)
        .reduce((map, group, groupIndex) => {
          const memo = group[0];
          const range = parseBukURL(memo.url).range;
          const type = getMemoGroupType(group);

          if (range) {
            const key = `${range}.${MOMO_MEMO_STYLE_CLASS}`;
            map.set(key, {
              index: groupIndex,
              type:
                type === 'mine'
                  ? MEMO_INDICATOR_TYPE_MINE
                  : type === 'others'
                  ? typeOthers
                  : typeMixed,
              focus:
                this._referer?.query?.mId &&
                !this._referer?.query?.cId &&
                group.findIndex((m) => m.id === this._referer?.query?.mId) !==
                  -1,
            });
          }

          return map;
        }, new Map<string, { index: number; type: string; focus: boolean }>());

      if (memoGroupInfoMap.size > 0) {
        const querySelector = Array.from(memoGroupInfoMap.keys())
          .map((hId) => `x.bukh-start[highlight-id="${hId}"]`)
          .join(',');

        iframe
          .contentDocument!.querySelectorAll(querySelector)
          .forEach((start) => {
            const div = start.ownerDocument.createElement('div');

            // 나의책 읽기인지 여부 판단
            const isMyBook =
              this._annotationsService.getState().groupId === undefined;

            if (isMyBook) {
              div.className = 'buk-memo-indicator my-book';
            } else {
              div.className = 'buk-memo-indicator';
              div.style.setProperty('background-color', '#5EFFFF', 'important');
            }

            const info = memoGroupInfoMap.get(
              start.getAttribute('highlight-id')!
            )!;
            div.dataset.groupIdx = info.index.toString();
            div.dataset.type = info.type;

            // 그룹 모드일 때만 도트 클릭 이벤트 활성화 및 커서 포인터 추가
            // 도트 클릭 이벤트 활성화
            div.addEventListener('click', (event) => {
              event.preventDefault();
              event.stopPropagation();
              (event as any).bukvEventHandled = true;
              this._onMemoIndicatorClick(event.target as HTMLElement);
            });

            if (appendToBody) {
              const rect = (start as HTMLElement).getClientRects()[0];

              div.style.left = rect.left + 'px';
              div.style.top = rect.top + 'px';

              start.ownerDocument.body.appendChild(div);
            } else {
              start.appendChild(div);
            }
          });
      }

      if (
        this._referer?.query?.cPreview != null &&
        this._referer?.query?.memo != null &&
        this._referer.iid === iid
      ) {
        iframe
          .contentDocument!.querySelectorAll(
            `x.bukh-start[highlight-id="${this._referer.anchor}.${MOMO_MEMO_STYLE_CLASS}"]`
          )
          .forEach((start) => {
            const div = start.ownerDocument.createElement('div');
            div.className = 'buk-memo-indicator';
            div.dataset.type = MEMO_INDICATOR_TYPE_COMMENTARY;

            // z index 내 메모랑 겹쳤을 때
            const rect = (start as HTMLElement).getClientRects()[0];

            div.style.left = rect.left + 'px';
            div.style.top = rect.top + 'px';

            start.ownerDocument.body.appendChild(div);
          });
      }
    };

    getActiveIframes(this._element.nativeElement, 'bukv-pdf-page').forEach(
      (iframe) => {
        updateMemoIndicator(iframe, true);
      }
    );

    getActiveIframes(this._element.nativeElement, 'bukv-page').forEach(
      (iframe) => {
        updateMemoIndicator(iframe, false);
      }
    );
  }

  private _onMemoIndicatorClick(indicatorElem: HTMLElement): void {
    const baseBukURL = indicatorElem.ownerDocument.documentElement.getAttribute(
      'data-bukv-base-buk-url'
    );

    if (!baseBukURL) {
      return;
    }

    // dot 클릭 시 임시 하이라이트 제거 이벤트 발생
    this._eventBusService.fire(
      'ContentsComponent:memoIndicatorDotClick',
      undefined
    );

    const iid = parseBukURL(baseBukURL).iid!;
    const groupIndex = parseInt(indicatorElem.dataset.groupIdx!);
    const memos = this._annotationsService.getMemosOfItem(iid)[groupIndex];
    const shortestMemo = memos[0]; // 첫 번째 메모 선택

    // 그룹 모드인지 확인
    const isGroupMode =
      this._annotationsService.getState().groupId !== undefined;

    const address = parseBukURL(shortestMemo.url);
    if (!address.query) {
      address.query = {};
    }

    if (isGroupMode) {
      // 그룹 모드일 때: 하이라이트 + 전체 댓글 탭으로 전환
      // _focusMemo를 호출하지 않음 (형광색 하이라이트 방지)

      address.query.highlight = '#5EFFFF33';
      this._openBook(address);

      // 전체 댓글 탭으로 전환하고 해당 메모로 스크롤
      this._ngZone.run(() => {
        // 댓글창이 열려있든 닫혀있든 항상 이벤트 발생
        // (열려있을 때도 _load$를 트리거해야 pending scroll이 소비됨)
        this._eventBusService.fire('RightPanelComponent:openRightPanelTab', {
          type: 'comment',
        });

        this._eventBusService.fire(
          'ReadingGroupMemoListComponent:scrollToMemo',
          {
            memoId: shortestMemo.id,
            url: shortestMemo.url,
            source: 'indicator', // indicator 클릭으로 인한 스크롤임을 표시
          }
        );
      });
    } else {
      // 단일 모드(나의 책)일 때: 도트 클릭 포커스 기능 비활성화
      // 아무 동작도 하지 않음

      address.query.highlight = 'rgba(224, 255, 0, 0.2)';
      this._openBook(address);

      this._ngZone.run(() => {
        // 내 기록창이 닫혀있다면 내 기록창 여는 이벤트 발생
        if (!this._uiStateService.isRightPanelOpened) {
          this._eventBusService.fire('RightPanelComponent:openRightPanelTab', {
            type: 'myAnnotations',
          });
        }

        // 내기록 메모를 찾고 스크롤 되도록 하는 이벤트 발생
        this._eventBusService.fire('CommentRightPanelComponent:scrollToMemo', {
          memoId: shortestMemo.id,
          source: 'memo-list',
        });
      });

      return;
    }
  }

  private _blinkMemo(url: string): void {
    const address = parseBukURL(url);

    if (!address.range) {
      return;
    }

    getActiveIframes(
      this._element.nativeElement,
      this._book?.meta.type === BookType.PDF ? 'bukv-pdf-page' : 'bukv-page'
    ).forEach((iframe) => {
      const baseBukURL = iframe.contentDocument!.documentElement.getAttribute(
        'data-bukv-base-buk-url'
      );

      if (!baseBukURL) {
        return;
      }

      if (url.indexOf(baseBukURL) === -1) {
        return;
      }

      const selector = `x[highlight-id="${address.range}.${MOMO_MEMO_STYLE_CLASS}"]`;
      const elem = iframe.contentDocument?.querySelectorAll(selector);

      if (elem) {
        elem.forEach((el) => {
          el.classList.add('animation');
        });

        setTimeout(() => {
          elem.forEach((el) => {
            el.classList.remove('animation');
          });
        }, 900);

        address.query = {
          highlight: 'transparent',
        };
        this._openBook(address);
      }
    });
  }

  private _clearFocusedMemo(): void {
    getActiveIframes(
      this._element.nativeElement,
      this._book?.meta.type === BookType.PDF ? 'bukv-pdf-page' : 'bukv-page'
    ).forEach((iframe) => {
      iframe.contentDocument
        ?.querySelectorAll(
          `.${FOCUSED_MEMO_CLASS_SINGLE}, .${FOCUSED_MEMO_CLASS_DOUBLE}, x.bukh-color-shared`
        )
        .forEach((elem) => {
          elem.classList.remove(
            FOCUSED_MEMO_CLASS_SINGLE,
            FOCUSED_MEMO_CLASS_DOUBLE
          );

          if (elem.classList.contains('bukh-color-shared')) {
            (elem as HTMLElement).outerHTML = (elem as HTMLElement).innerHTML;
          }
        });
    });
  }

  private _focusMemo(
    url: string,
    type: 'single' | 'double',
    scroll?: boolean
  ): void {
    const address = parseBukURL(url);

    if (!address.range) {
      return;
    }

    this._clearFocusedMemo();

    getActiveIframes(
      this._element.nativeElement,
      this._book?.meta.type === BookType.PDF ? 'bukv-pdf-page' : 'bukv-page'
    ).forEach((iframe) => {
      const baseBukURL = iframe.contentDocument!.documentElement.getAttribute(
        'data-bukv-base-buk-url'
      );

      if (!baseBukURL) {
        return;
      }

      if (url.indexOf(baseBukURL) === -1) {
        return;
      }

      const selector = `x[highlight-id="${address.range}.${MOMO_MEMO_STYLE_CLASS}"]`;
      const elem = iframe.contentDocument?.querySelectorAll(selector);

      if (elem) {
        elem.forEach((el) => {
          el.classList.add(
            type === 'single'
              ? FOCUSED_MEMO_CLASS_SINGLE
              : FOCUSED_MEMO_CLASS_DOUBLE
          );
        });

        if (scroll) {
          address.query = {
            highlight: 'transparent',
          };
          this._openBook(address);
        }
      }
    });
  }

  private _onCommentaryPreviewMemoIndicatorClick(event: MouseEvent): void {
    if (!this._referer?.query?.cPreview) {
      return;
    }

    this._focusMemo(this._referer.toString(), 'single');

    this._ngZone.run(() => {
      this._eventBusService.fire(
        'ContentsComponent:commentaryPreviewMemoIndicatorClick',
        {
          memo: {
            avatarImageURL: this._referer!.query!.avatar,
            nickname: this._referer!.query!.nickname,
            text: this._referer!.query!.text,
            content: this._referer!.query!.memo!,
          },
          trigger: event.currentTarget! as HTMLElement,
        }
      );
    });
  }

  private _onPDFTextLoadAfterItemLoad(): void {
    // 약간의 지연을 두어 DOM 렌더링 완료 대기
    setTimeout(() => {
      this._highlightRefererRange();
      this._highlightPendingAddress();
    }, 100);

    this._updateMemoIndicator();
  }

  public suppressNextRefererHighlight(): void {
    this._suppressNextRefererHighlight = true;
  }

  private _highlightRefererRange(): void {
    if (this._suppressRefererHighlightForPendingComment) {
      return;
    }

    if (this._suppressNextRefererHighlight) {
      this._suppressNextRefererHighlight = false;
      return;
    }

    if (!this._referer?.range) {
      return;
    }

    // 책장에서 넘어온 경우, 내 메모 포커스인 경우 하이라이트 안함
    if (
      this._referer.query?.bookshelf != null ||
      this._referer.query?.mId != null
    ) {
      return;
    }

    const viewerAddress = this._viewerComp.getCurrentAddress();

    if (!viewerAddress) {
      return;
    }

    const currentIid = viewerAddress.iid ?? this._book?.items[0].iid;

    if (this._referer.iid !== currentIid) {
      return;
    }

    // bukh-anchor가 DOM에 이미 존재하면 bukh-color-shared를 그리지 않음
    const hasAnchorHighlight = getActiveIframes(
      this._element.nativeElement,
      this._book?.meta.type === BookType.PDF ? 'bukv-pdf-page' : 'bukv-page'
    ).some((iframe) =>
      iframe.contentDocument?.querySelector(
        `x.${Config.highlightClass.anchorHighlight}`
      )
    );

    if (hasAnchorHighlight) {
      return;
    }

    // 기본 색상: 푸른색
    let highlightStyleClass = SHARED_HIGHLIGHT_STYLE_CLASS;

    // 코멘터리 미리보기 시 코멘터리 메모 또는 하이라이트 스타일 적용
    if (this._referer.query?.cPreview) {
      highlightStyleClass =
        this._referer.query.memo != null
          ? MOMO_MEMO_STYLE_CLASS
          : MOMO_HIGHLIGHT_STYLE_CLASS_COMMENTARY;
    }

    try {
      this._viewerComp.createHighlight(
        this._referer.toString(),
        highlightStyleClass,
        { expandToWord: false, emitEvent: false }
      );
    } catch (error) {
      console.log(error);
    }
  }

  private _highlightPendingAddress(): void {
    if (!this._pendingHighlightAddress?.range) {
      return;
    }

    const viewerAddress = this._viewerComp.getCurrentAddress();

    if (!viewerAddress) {
      return;
    }

    const currentIid = viewerAddress.iid ?? this._book?.items[0].iid;

    // 현재 아이템과 주소의 아이템이 일치하는 경우에만 하이라이트 적용
    if (this._pendingHighlightAddress.iid !== currentIid) {
      this._pendingHighlightAddress = undefined;
      return;
    }

    try {
      this._createHighlight(
        'memo',
        this._pendingHighlightAddress.toString(),
        'buk-temp-comment-highlight',
        false
      );
      // 하이라이트 적용 후 주소 초기화
      this._pendingHighlightAddress = undefined;
    } catch (error) {
      console.log('Failed to highlight pending address:', error);
      this._pendingHighlightAddress = undefined;
    }
  }

  private _destroyFrameBodyMutationObserver(): void {
    this._frameBodyMutationObserver?.disconnect();
    this._frameBodyMutationObserver = undefined;
  }

  public _onSettingsChanged(
    property: keyof Settings,
    value: unknown,
    applyHighlights?: boolean
  ): void {
    if (!this._viewerComp) {
      return;
    }

    if (property === 'fontFace' || property === 'fontSize') {
      this._sharedPageInfo = null;
    }

    this._viewerComp.updateSettings(property, value, applyHighlights);
  }

  private _openSettingsDialog(): void {
    this._dialogService.open(SettingsDialogComponent, {
      data: {
        forceDisableSettingsKeys: this._shouldFixPagingMode
          ? ['pagingMode']
          : undefined,
        currentSettings: this._viewerComp.getCurrentSettings(),
        availableFonts: this._viewerComp.getFontsCurrentlyAvailable(),
        onChange: (property: keyof Settings, value: unknown): void => {
          this._onSettingsChanged(property, value);
        },
      },
    });
  }

  private _openBookInfoDialog(): void {
    this._dialogService
      .open(BookInfoDialogComponent, {
        data: {
          book: this._book!,
          currentTOCIndex: this._currentTOCIndex,
        },
      })
      .afterClosed()
      .subscribe((result) => {
        result && this._openBook(result.url);
      });
  }

  private _openReadingModeDialog(): void {
    if (!this._book) {
      return;
    }

    if (this._book.meta.is_subcontent) {
      this._toastService.openWarning(
        '부록은 혼자읽기 모드로만 읽기가 가능합니다'
      );
      return;
    }

    if (!this._book.isOwnedByUser) {
      this._toastService.openWarning(
        '미리보기 중에는 읽기 모드 변경이 불가능합니다.'
      );
      return;
    }

    this._dialogService.open(ReadingModeDialogComponent);
  }

  public getCurrentSettings(): boolean {
    return this._viewerComp?.getCurrentSettings().multiColumn.value;
  }

  private _clearTemporaryCommentHighlight(): void {
    getActiveIframes(this._element.nativeElement).forEach((iframe) => {
      const doc = iframe.contentDocument;
      if (!doc) {
        return;
      }

      const allTempHighlights = doc.querySelectorAll(
        '.buk-temp-comment-highlight'
      );

      allTempHighlights.forEach((element) => {
        element.setAttribute('class', '');
      });
    });
  }

  private _clearAnchorHighlight(): void {
    getActiveIframes(
      this._element.nativeElement,
      this._book?.meta.type === BookType.PDF ? 'bukv-pdf-page' : 'bukv-page'
    ).forEach((iframe) => {
      const doc = iframe.contentDocument;
      if (!doc) {
        return;
      }

      // bukh-anchor와 bukh-shared 클래스를 가진 x 태그 모두 제거
      doc
        .querySelectorAll(
          `x.${Config.highlightClass.anchorHighlight}, x.bukh-color-shared`
        )
        .forEach((elem) => {
          // x 태그를 제거하고 내부 콘텐츠만 남김
          (elem as HTMLElement).outerHTML = (elem as HTMLElement).innerHTML;
        });
    });
  }
}
